import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { isLanguage } from '../core/i18n/languages';
import { CookieBanner } from './cookie-banner';
import { LanguageAlternatesProvider } from './language-alternates';
import { useSiteAnalytics } from './use-site-analytics';

/**
 * Every page lives under a `/:lang` prefix. An unknown prefix is not a 404 — it is more likely an
 * old or mistyped link than a missing page — so it redirects to Kazakh rather than dead-ending.
 */
export function LanguageLayout() {
    const { lang } = useParams<{ lang: string }>();

    // Above every page and below none. The landing page, the booking wizard and the legal page each
    // carry their own chrome and share no header, so this is the only layer all three pass through
    // — which is where both the consent question and the reporting that depends on it belong.
    useSiteAnalytics();

    // `<html lang>` follows the URL. The build writes it into each prerendered file, and this keeps
    // it right across a client-side language switch — the live site never updates it at all, which
    // is how `/ru/` comes to announce itself as Kazakh to a screen reader and a translator alike.
    useEffect(() => {
        if (isLanguage(lang)) {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    if (!isLanguage(lang)) {
        return <Navigate to="/kk" replace />;
    }

    // The provider wraps the whole tree, header included: the switcher lives in the header and
    // reads what the page below it published. The chrome itself is a route rather than something
    // rendered here, because the landing page brings its own — see `SiteChrome`.
    return (
        <LanguageAlternatesProvider>
            <Outlet />
            <CookieBanner />
        </LanguageAlternatesProvider>
    );
}
